import { useFormik } from 'formik'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { useDebounce } from 'use-debounce'
import * as Yup from 'yup'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import Dropdown from '../../../components/v2/Dropdown'
import GenderCard from '../../../components/v2/GenderCard'
import Input from '../../../components/v2/Input'
import RemainingCompanies from '../../../components/v2/RemainingCompanies'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import PetProgressbar from '../../../components/v2/progrees-bars/PetProgressBar'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { PetState, setBreedId, setPetFields } from '../../../store/petSlice'

const PetInfo = () => {
  //
  const dispatch = useDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { filter, screenHeader } = useAppSelector((data: { pet: PetState }) => data.pet)
  //
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetPetPageInfoQuery('pet-info')
  //
  const { data: petBreed, isLoading: isPetBreedLoading } = api.useGetPetBreedsQuery(undefined)
  //
  const { data: petCategories, isLoading: isPetCategoryLoading } = api.useGetPetCategoriesQuery(undefined)
  //
  //
  const pageTitle = pageInfo?.translations[lang]?.title
  const stepName = pageInfo?.translations[lang]?.stepName
  //
  const breeds = petBreed?.filter((x) => x.categoryId === filter?.petCategoryId)
  //
  const [debouncedBreedOther] = useDebounce(filter?.petInfo?.breedOther, 1000)
  //
  const [getLeftCompanies, { data: leftCompanies }] = api.usePetLeftCompaniesMutation()
  //
  useEffect(() => {
    if (
      (filter.breedId || filter.breedId === null) &&
      ((filter?.petInfo?.month !== null && filter?.petInfo?.month !== 0) ||
        (filter?.petInfo?.age !== null && filter?.petInfo?.age !== 0)) &&
      (filter?.breedId === null ? debouncedBreedOther !== '' : true)
    ) {
      //
      const GetCompaniesFunction = async () => {
        try {
          await getLeftCompanies({
            step: 'one',
            animalCategoryId: filter?.petCategoryId as number,
            breedId: filter.breedId,
            petAge: {
              month: filter?.petInfo?.month as number,
              year: filter?.petInfo?.age as number,
            },
            otherBreedName: filter?.breedId === null ? debouncedBreedOther : '',
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (GetCompaniesFunction) (single)')
          }
        }
      }
      //
      GetCompaniesFunction()
    }

    return () => {}
  }, [
    getLeftCompanies,
    petCategories,
    filter?.breedId,
    filter?.petCategoryId,
    filter?.petInfo?.age,
    filter?.petInfo?.month,
    debouncedBreedOther,
  ])
  //
  //
  //
  // english letter regex
  const englishLetterRegex = /^[A-Za-z]+$/
  //
  // georgian letter regex
  const georgianLetterRegex = /^[\u10D0-\u10FF]*$/
  //
  const letterRegex = lang === 'ge' ? georgianLetterRegex : englishLetterRegex

  //
  const ValidationSchema = Yup.object({
    color: Yup.string()
      .matches(letterRegex, lang === 'ge' ? `${t('onlyGeorgianLetters')}` : `${t('onlyEnglishLetters')}`)
      .required(`${t('required')}`),
    breed: Yup.string().required(`${t('required')}`),
    breedOther: Yup.string().when('breed', {
      is: (breed: string) => breed === 'other',
      then: Yup.string()
        .matches(/^\S*$/, `${t('spaceValidation')}`)
        .matches(georgianLetterRegex, `${t('onlyGeorgianLetters')}`)
        .required(`${t('required')}`),
      otherwise: Yup.string().notRequired(),
    }),
    gender: Yup.string().required(`${t('required')}`),
  })
  //
  const initialValues = {
    color: filter.petInfo.color || '',
    breed: filter.petInfo.breed || '',
    breedOther: filter.petInfo.breedOther || '',
    gender: filter.petInfo.gender || '',
  }
  // formi state
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: ValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: () => {},
  })
  //
  //@ts-ignore
  const replacedPageTitle = pageTitle?.replace('petName', filter?.petInfo?.name)
  //@ts-ignore
  const replacedStepName = stepName?.replace('petName', filter?.petInfo?.name)
  //
  //
  const updatedPetBreed = [
    ...(breeds || []),
    {
      id: 0,
      status: 'published',
      translations: {
        ge: {
          name: 'სხვა',
        },
        en: {
          name: 'Other',
        },
      },
    },
  ]?.filter((x) => x.status === 'published')
  //
  //
  // auto navigate first page
  useEffect(() => {
    if (filter?.petCategoryId === null) {
      navigate('/choose-pet')
    }
    //
    return () => {}
  }, [filter?.petCategoryId, navigate])

  const MainLoading = isPageInfoLoading || isPetCategoryLoading || isPetBreedLoading
  //
  //
  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{replacedPageTitle}</title>
        <meta name="description" content={replacedPageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />

      <Container>
        <Content>
          <Title text={replacedPageTitle} />

          <PetProgressbar stepName={replacedStepName} />

          <WhiteBox>
            <BoxTitle>{filter?.petInfo?.name}</BoxTitle>

            <Inputs>
              <Dropdown
                title={t('age')}
                isSearchable={false}
                isDisabled={false}
                value={[0, 1, 2, 3, 4, 5, 6, 7, 8].find((x) => x === filter?.petInfo?.age)}
                options={[
                  ...[0, 1, 2, 3, 4, 5, 6, 7, 8].map((c) => ({
                    value: c,
                    label: `${c} ${t('year')}`,
                  })),
                ]}
                onChange={(target) => [
                  dispatch(
                    setPetFields({
                      fieldName: 'age',
                      fieldValue: target.value,
                    }),
                  ),
                ]}
              />

              {filter.petInfo.age && filter?.petInfo?.age >= 8 ? null : (
                <Dropdown
                  title={t('month')}
                  isSearchable={false}
                  isDisabled={false}
                  value={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].find((x) => x === filter?.petInfo?.month)}
                  options={[
                    ...[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((c) => ({
                      value: c,
                      label: `${c} ${t('month')}`,
                    })),
                  ]}
                  onChange={(target) => [
                    dispatch(
                      setPetFields({
                        fieldName: 'month',
                        fieldValue: target.value,
                      }),
                    ),
                    // formik.setFieldValue('month', target.value),
                  ]}
                  // error={formik.errors.month && filter.petInfo.month ? true : false}
                  // onBlur={formik.handleBlur}
                />
              )}

              <Input
                id="pet-color"
                name="pet-color"
                label={t('color')}
                width={'fullWidth'}
                type="text"
                value={filter.petInfo.color || ''}
                onChange={(value) => [
                  dispatch(
                    setPetFields({
                      fieldName: 'color',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('color', value),
                ]}
                error={formik.errors.color && filter.petInfo.color ? true : false}
                errorText={formik.errors.color}
                onBlur={formik.handleBlur}
              />

              <Dropdown
                title={t('breed')}
                isSearchable={true}
                isDisabled={false}
                value={filter?.petInfo?.breed}
                options={[
                  ...updatedPetBreed?.map((c) => ({
                    value: c.translations[lang]?.name,
                    label: c.translations[lang]?.name,
                  })),
                ]}
                onChange={(target) => {
                  let breedId = updatedPetBreed?.find((x) => x.translations[lang]?.name === target.value)?.id

                  return [
                    //
                    dispatch(
                      setPetFields({
                        fieldName: 'breed',
                        fieldValue: target.value,
                      }),
                    ),
                    //
                    dispatch(setBreedId(breedId ? breedId : null)),
                    //
                    formik.setFieldValue('breed', target.value),
                  ]
                }}
                error={formik.errors.breed && filter.petInfo.breed ? true : false}
                onBlur={formik.handleBlur}
              />

              {filter?.petInfo?.breed === 'Other' || filter?.petInfo?.breed === 'სხვა' ? (
                <Input
                  id="pet-breed-name"
                  name="pet-breed-name"
                  label={t('addBreed')}
                  width={'fullWidth'}
                  type="text"
                  value={filter.petInfo.breedOther || ''}
                  onChange={(value) => [
                    dispatch(
                      setPetFields({
                        fieldName: 'breedOther',
                        fieldValue: value,
                      }),
                    ),
                    formik.setFieldValue('breedOther', value),
                  ]}
                  error={formik.errors.breedOther && filter.petInfo.breedOther ? true : false}
                  errorText={formik.errors.breedOther}
                  onBlur={formik.handleBlur}
                />
              ) : null}

              <>
                <ChooseGenderLabel>{t('chooseGender')}</ChooseGenderLabel>

                <GenderBlock>
                  {['female', 'male'].map((item, index) => (
                    <GenderCard
                      key={index}
                      pet={true}
                      selected={filter.petInfo?.gender === item}
                      gender={item}
                      femaleText={`${t('petFemale')}`}
                      maleText={`${t('petMale')}`}
                      onClick={() => [
                        dispatch(
                          setPetFields({
                            fieldName: 'gender',
                            fieldValue: item,
                          }),
                        ),
                        formik.setFieldValue('gender', item),
                      ]}
                      error={formik.errors.gender && filter.petInfo.gender ? true : false}
                      errorText={formik.errors.gender}
                      onBlur={formik.handleBlur}
                    />
                  ))}
                </GenderBlock>
              </>
            </Inputs>
          </WhiteBox>

          <RemainingCompanies
            style={{
              opacity: leftCompanies ? '1' : '0',
              marginInline: 'auto',
            }}
            text={`${t('offerAvailable')}: ${leftCompanies?.companiesCount} `}
          />

          <Buttons>
            <NavigationButton text={`${t('back')}`} buttonType="prev" onClick={() => navigate('/choose-pet')} />
            <NavigationButton
              text={`${t('next')}`}
              buttonType="next"
              disabled={
                formik.isValid &&
                ((filter?.petInfo?.month !== null && filter?.petInfo?.month !== 0) ||
                  (filter?.petInfo?.age !== null && filter?.petInfo?.age !== 0)) &&
                (filter?.breedId === null ? debouncedBreedOther !== '' : true) &&
                filter?.petInfo?.color &&
                filter?.petInfo?.gender &&
                filter?.petInfo?.breed &&
                leftCompanies &&
                leftCompanies?.companiesCount &&
                leftCompanies?.companiesCount > 0
                  ? false
                  : true
              }
              onClick={() => {
                if (filter.petInfo.age && filter.petInfo.age >= 8) {
                  return [
                    navigate('/pet-start-date'),
                    setPetFields({
                      fieldName: 'month',
                      fieldValue: null,
                    }),
                  ]
                } else {
                  return navigate('/pet-start-date')
                }
              }}
            />
          </Buttons>
        </Content>
      </Container>
      <BackgroundIcon />
    </Wrapper>
  )
}
//
//
const ChooseGenderLabel = styled.label`
  font-size: 14px;
  margin-bottom: -10px;
  color: ${Colors.primaryGrey};
`
const GenderBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
`
const Inputs = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 20px;
  margin-top: 20px;
`
const BoxTitle = styled.h6`
  font-size: 18px;
  color: ${Colors.primaryBlack};
`
const WhiteBox = styled.div`
  background-color: ${Colors.white};
  margin-block: 32px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  max-width: 464px;
  margin-inline: auto;
  border-radius: 8px;
  padding: 34px 48px;
  position: sticky;
  z-index: 1;

  ${Responsive.mobile} {
    padding: 20px;
  }
`
const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`
const Content = styled.div`
  padding-block: 60px;
  position: relative;
  z-index: 2;

  ${Responsive.mobile} {
    padding-block: 30px;
  }
`
const Wrapper = styled.div`
  /*  */
`
//
export default PetInfo
